import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
            title={article.title}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            title={article.title}
          />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            {article.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
                {tag}
              </span>
            ))}
          </div>
          <h3 className="text-lg font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-4">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={article.title}
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {article.tags.slice(0, 1).map((tag) => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
              {tag}
            </span>
          ))}
        </div>
        <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span>{article.date}</span>
          <span>·</span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}